import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { adminApi } from '../lib/adminApi';
import { Webhook, RefreshCw, CheckCircle2, AlertTriangle } from 'lucide-react';

const AdminPaymentEventsPanel = () => {
  const { toast } = useToast();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.listPaymentEvents({ limit: 200 });
      setData(r);
    } catch (err) {
      toast({ title: 'Failed to load events', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const fmt = (iso) => iso ? new Date(iso).toLocaleString('en-IN') : '—';

  return (
    <div className="space-y-4" data-testid="payment-events-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Webhook className="w-5 h-5 text-[#2c54b2]" /> Razorpay Webhook Events
          </h2>
          <p className="text-xs text-gray-500 mt-0.5">Real-time payment / subscription / refund callbacks from Razorpay. Each entry is signature-verified before processing.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="events-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading…</div>
        ) : data.items.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No webhook events received yet. Configure the webhook URL in Razorpay Dashboard → Webhooks.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {data.items.map((e) => (
              <div key={e.id} className="px-3 py-2 hover:bg-gray-50" data-testid={`event-row-${e.id}`}>
                <button onClick={() => setExpanded(expanded === e.id ? null : e.id)} className="w-full text-left flex items-center gap-3">
                  {e.verified
                    ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                    : <AlertTriangle className="w-4 h-4 text-red-600 shrink-0" />}
                  <span className="font-mono text-xs bg-blue-50 text-[#2c54b2] px-1.5 py-0.5 rounded">{e.event}</span>
                  <span className="text-xs text-gray-500 flex-1">{fmt(e.received_at)}</span>
                  {e.razorpay_event_id && <span className="text-[10px] text-gray-400 font-mono">{e.razorpay_event_id}</span>}
                </button>
                {expanded === e.id && (
                  <pre className="text-[11px] bg-gray-900 text-gray-100 p-3 mt-2 rounded overflow-x-auto max-h-80">
                    {JSON.stringify(e.payload || e.raw || e, null, 2)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPaymentEventsPanel;
